import { RTCPeer } from "./rtc/RTCPeer.js";
import { SignalingClient } from "./rtc/SignalingClient.js";
import * as utils from "./utils.js";

const iceServers = [
    { urls: "stun:stun.l.google.com:19302" },
    // { urls: "stun:stun.l.google.com:5349" },
    // { urls: "stun:stun1.l.google.com:3478" },
    // { urls: "stun:stun1.l.google.com:5349" },
    // { urls: "stun:stun2.l.google.com:19302" },
    // { urls: "stun:stun2.l.google.com:5349" },
    // { urls: "stun:stun3.l.google.com:3478" },
    // { urls: "stun:stun3.l.google.com:5349" },
    { urls: "stun:stun4.l.google.com:19302" },
    // { urls: "stun:stun4.l.google.com:5349" },
];

/**
 * The signaling client config.
 * @typedef SignalingConfig 
 * @property {string =} signalingServer - The signaling server's address (e.g. 
 *      `https://example.com:1234`).
 * @property {(response: {roomId: string}) => void =} handleJoinResponse - 
 *      Handler to call when the client joins a room.
 */

export default class P2PNetwork {
    /** @type Readonly<SignalingConfig> */
    static _DEFAULT_SIGNALING_CONFIG = Object.freeze({
        signalingServer: "http://localhost:3333",
        handleJoinResponse: () => { },
    });

    /** @type RTCPeer? */
    _rtcPeer = null;

    /** @type SignalingClient? */
    _signalingClient = null;

    /**
     * @param {SignalingConfig} signalingConfig - The signaling client config.
     * @param {object} dcConfig - The data channel configuration.
     * @param {() => void =} dcConfig.onOpen - Handler to call when the data channel is ready.
     * @param {(data: any) => void =} dcConfig.onMessage - Handler to call when a message
     *      is received from the other peer.
     * @param {() => void =} dcConfig.onClose - Handler to call when the data channel is closed.
     */
    constructor(signalingConfig, dcConfig) {
        this._signalingConfig = {
            ...P2PNetwork._DEFAULT_SIGNALING_CONFIG,
            ...signalingConfig
        };
        this._dcConfig = dcConfig;
    }

    initConnection() {
        const { onOpen, onMessage, onClose } = this._dcConfig
        this._rtcPeer?.close();
        this._rtcPeer = new RTCPeer({ iceServers }, {
            open: () => {
                utils.log("[p2p] --- Connection open ---");
                if (onOpen) onOpen();
            },
            message: (msg) => {
                if (onMessage) onMessage(JSON.parse(msg.data));
            },
            close: () => {
                utils.log("[p2p] --- Connection closed ---");
                if (onClose) onClose();
            }
        });

        this._signalingClient?.disconnect();

        const { signalingServer, handleJoinResponse } = this._signalingConfig;
        this._signalingClient =
            //@ts-ignore
            new SignalingClient(signalingServer, this._rtcPeer, {
                handleJoinResponse,
                dataChannelName: "defaultDataChannel",
                handlePeerLeft: () => {
                    utils.log("[p2p] Peer left, waiting for reconnection...");
                },
            });

        this._signalingClient.createOrJoinRoom(utils.getRoomId());
    }

    /**
     * Sends data to the peer.
     * @param {any} data - Any value that can be safely passed to JSON.stringify
     *      (i.e., JSON-serializable).
     */
    send(data) {
        if (!this._signalingClient || !this._rtcPeer) {
            throw new Error("Connection must be initialized using "
                + "'initConnection()' prior to sending data");
        }
        this._rtcPeer.dataChannel?.send(JSON.stringify(data));
    }

    /** Explicitly close the peer-to-peer connection and all its resources. */
    disconnect() {
        this._rtcPeer?.close();
        this._signalingClient?.disconnect();
    }
}
